'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface BmsSistema {
  id: string;
  nome: string;
  produttore: string;
  versione: string;
  ip_address: string;
  porta: number;
  protocollo: string;
  stato: string;
  ultima_sincronizzazione: string;
  intervallo_polling_sec: number;
  note: string;
}

interface BmsPuntoDato {
  id: string;
  bms_id: string;
  nome: string;
  indirizzo_registro: string;
  tipo_dato: string;
  valore_attuale: string;
  unita_misura: string;
  ultima_lettura: string;
  zona_id: string;
  zona?: { id: string; nome: string };
}

const BMS_STATO_BG: Record<string, string> = {
  connesso: 'bg-emerald-500/20 text-emerald-400',
  disconnesso: 'bg-gray-500/20 text-gray-400',
  errore: 'bg-red-500/20 text-red-400',
  sincronizzazione: 'bg-sky-500/20 text-sky-400',
};

export default function BmsIntegrazioneContent() {
  const [sistemi, setSistemi] = useState<BmsSistema[]>([]);
  const [puntiDati, setPuntiDati] = useState<BmsPuntoDato[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBms, setSelectedBms] = useState<string | null>(null);
  const [showAddBms, setShowAddBms] = useState(false);
  const [editBms, setEditBms] = useState<BmsSistema | null>(null);
  const [showAddPunto, setShowAddPunto] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string; table: string }>({ open: false, id: '', name: '', table: '' });
  const [zone, setZone] = useState<{ id: string; nome: string }[]>([]);
  const [formBms, setFormBms] = useState({ nome: '', produttore: '', versione: '', ip_address: '', porta: '502', protocollo: 'modbus', intervallo_polling_sec: '60', note: '' });
  const [formPunto, setFormPunto] = useState({ bms_id: '', nome: '', indirizzo_registro: '', tipo_dato: 'float', unita_misura: '', zona_id: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [bmsRes, puntiRes, zoneRes] = await Promise.all([
        supabase.from('bms_sistemi').select('*').order('nome'),
        supabase.from('bms_punti_dati').select('*, zona:zone_fisiche(id,nome)').order('nome'),
        supabase.from('zone_fisiche').select('id,nome').order('nome'),
      ]);
      if (bmsRes.error) throw bmsRes.error;
      if (puntiRes.error) throw puntiRes.error;
      if (zoneRes.error) throw zoneRes.error;
      setSistemi(bmsRes.data || []);
      setPuntiDati(puntiRes.data || []);
      setZone(zoneRes.data || []);
      if (!selectedBms && (bmsRes.data || []).length > 0) {
        setSelectedBms(bmsRes.data[0].id);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBms]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddBms = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formBms.nome.trim()) { setError('Nome obbligatorio'); return; }
    const porta = parseInt(formBms.porta);
    if (isNaN(porta) || porta < 1 || porta > 65535) { setError('Porta deve essere tra 1 e 65535'); return; }
    setSaving(true);
    try {
      const payload = {
        nome: formBms.nome,
        produttore: formBms.produttore,
        versione: formBms.versione,
        ip_address: formBms.ip_address,
        porta,
        protocollo: formBms.protocollo,
        intervallo_polling_sec: parseInt(formBms.intervallo_polling_sec) || 60,
        note: formBms.note,
      };
      if (editBms) {
        const { error: err } = await supabase.from('bms_sistemi').update(payload).eq('id', editBms.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('bms_sistemi').insert({ ...payload, stato: 'disconnesso' });
        if (err) throw err;
      }
      setShowAddBms(false);
      setEditBms(null);
      setFormBms({ nome: '', produttore: '', versione: '', ip_address: '', porta: '502', protocollo: 'modbus', intervallo_polling_sec: '60', note: '' });
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBms = async () => {
    try {
      const { error: err } = await supabase.from(deleteConfirm.table).delete().eq('id', deleteConfirm.id);
      if (err) throw err;
      setDeleteConfirm({ open: false, id: '', name: '', table: '' });
      fetchData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const aggiornaSincronizzazione = async (bmsId: string) => {
    await supabase.from('bms_sistemi').update({ ultima_sincronizzazione: new Date().toISOString(), stato: 'sincronizzazione' }).eq('id', bmsId);
    setTimeout(async () => {
      await supabase.from('bms_sistemi').update({ stato: 'connesso' }).eq('id', bmsId);
      fetchData();
    }, 2000);
    fetchData();
  };

  const selectedBmsData = sistemi.find(s => s.id === selectedBms);
  const puntiDelBms = puntiDati.filter(p => p.bms_id === selectedBms);

  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Integrazione BMS</h1>
            <p className="text-muted-color text-sm mt-1">Building Management System — connessione e punti dati</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors text-sm font-semibold">
              <Icon name="ArrowPathIcon" size={16} />
              Aggiorna
            </button>
            <button onClick={() => setShowAddBms(true)} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold">
              <Icon name="PlusIcon" size={16} />
              Aggiungi BMS
            </button>
          </div>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">{error}</div>}

        {/* KPI */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Sistemi BMS</p>
            <p className="text-2xl font-bold text-white">{sistemi.length}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Connessi</p>
            <p className="text-2xl font-bold text-emerald-400">{sistemi.filter(s => s.stato === 'connesso').length}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Punti Dati Totali</p>
            <p className="text-2xl font-bold text-white">{puntiDati.length}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Punti Selezionato</p>
            <p className="text-2xl font-bold text-sky-400">{puntiDelBms.length}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista BMS */}
          <div className="space-y-3">
            <h2 className="text-white font-semibold">Sistemi BMS</h2>
            {sistemi.map(bms => (
              <div
                key={bms.id}
                onClick={() => setSelectedBms(bms.id)}
                className={`bg-surface rounded-xl border p-4 cursor-pointer transition-all ${selectedBms === bms.id ? 'border-sky-500' : 'border-border hover:border-sky-500/50'}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-white font-semibold text-sm">{bms.nome}</p>
                    <p className="text-muted-color text-xs">{bms.produttore} v{bms.versione}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${BMS_STATO_BG[bms.stato] || 'bg-gray-500/20 text-gray-400'}`}>
                      {bms.stato}
                    </span>
                    <button onClick={e => { e.stopPropagation(); openEditBms(bms); }} className="p-1 rounded hover:bg-sky-500/10 text-muted-color hover:text-sky-400 transition-colors" title="Modifica">
                      <Icon name="PencilIcon" size={12} />
                    </button>
                    <button onClick={e => { e.stopPropagation(); setDeleteConfirm({ open: true, id: bms.id, name: bms.nome, table: 'bms_sistemi' }); }} className="p-1 rounded hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors" title="Elimina">
                      <Icon name="TrashIcon" size={12} />
                    </button>
                  </div>
                </div>
                <div className="space-y-1 text-xs text-muted-color">
                  <p className="font-mono">{bms.ip_address}:{bms.porta} ({bms.protocollo.toUpperCase()})</p>
                  <p>Polling: ogni {bms.intervallo_polling_sec}s</p>
                  {bms.ultima_sincronizzazione && (
                    <p>Ultima sync: {new Date(bms.ultima_sincronizzazione).toLocaleString('it-IT')}</p>
                  )}
                </div>
                <button
                  onClick={e => { e.stopPropagation(); aggiornaSincronizzazione(bms.id); }}
                  className="mt-3 w-full text-xs px-3 py-1.5 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors"
                >
                  Sincronizza
                </button>
              </div>
            ))}
            {sistemi.length === 0 && (
              <div className="text-center py-8 text-muted-color text-sm">Nessun sistema BMS configurato</div>
            )}
          </div>

          {/* Punti Dati */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white font-semibold">
                Punti Dati {selectedBmsData ? `— ${selectedBmsData.nome}` : ''}
              </h2>
              {selectedBms && (
                <button onClick={() => setShowAddPunto(true)} className="flex items-center gap-1 text-xs px-3 py-1.5 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors">
                  <Icon name="PlusIcon" size={14} />
                  Aggiungi Punto
                </button>
              )}
            </div>
            <div className="bg-surface rounded-xl border border-border overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">NOME</th>
                    <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">REGISTRO</th>
                    <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">VALORE</th>
                    <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">ZONA</th>
                    <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">LETTURA</th>
                    <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">AZIONI</th>
                  </tr>
                </thead>
                <tbody>
                  {puntiDelBms.map((p, i) => (
                    <tr key={p.id} className={`border-b border-border hover:bg-surface-elevated transition-colors ${i % 2 !== 0 ? 'bg-surface-elevated/20' : ''}`}>
                      <td className="px-4 py-3 text-sm font-semibold text-white">{p.nome}</td>
                      <td className="px-4 py-3 font-mono text-xs text-secondary-color">{p.indirizzo_registro}</td>
                      <td className="px-4 py-3 text-xs text-secondary-color">{p.tipo_dato}</td>
                      <td className="px-4 py-3 text-sm text-white font-semibold">{p.valore_attuale || '—'} <span className="text-muted-color text-xs">{p.unita_misura}</span></td>
                      <td className="px-4 py-3 text-xs text-muted-color">{p.zona?.nome || '—'}</td>
                      <td className="px-4 py-3 text-xs text-muted-color">{p.ultima_lettura ? new Date(p.ultima_lettura).toLocaleString('it-IT') : '—'}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => setDeleteConfirm({ open: true, id: p.id, name: p.nome, table: 'bms_punti_dati' })} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors" title="Elimina">
                          <Icon name="TrashIcon" size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {puntiDelBms.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-color text-sm">
                      {selectedBms ? 'Nessun punto dati configurato' : 'Seleziona un sistema BMS'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Modal Aggiungi BMS */}
        {showAddBms && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Aggiungi Sistema BMS</h3>
                <button onClick={() => setShowAddBms(false)} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
              </div>
              <form onSubmit={handleAddBms} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Nome *</label>
                  <input value={formBms.nome} onChange={e => setFormBms(p => ({ ...p, nome: e.target.value }))} required
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Produttore</label>
                    <input value={formBms.produttore} onChange={e => setFormBms(p => ({ ...p, produttore: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Versione</label>
                    <input value={formBms.versione} onChange={e => setFormBms(p => ({ ...p, versione: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">IP Address</label>
                    <input value={formBms.ip_address} onChange={e => setFormBms(p => ({ ...p, ip_address: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Porta</label>
                    <input type="number" value={formBms.porta} onChange={e => setFormBms(p => ({ ...p, porta: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Protocollo</label>
                    <select value={formBms.protocollo} onChange={e => setFormBms(p => ({ ...p, protocollo: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                      <option value="modbus">Modbus TCP</option>
                      <option value="OPC-UA">OPC-UA</option>
                      <option value="bacnet">BACnet</option>
                      <option value="knx">KNX</option>
                      <option value="altro">Altro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Polling (sec)</label>
                    <input type="number" value={formBms.intervallo_polling_sec} onChange={e => setFormBms(p => ({ ...p, intervallo_polling_sec: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowAddBms(false)} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
                  <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold disabled:opacity-50">
                    {saving ? 'Salvataggio...' : 'Salva'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Aggiungi Punto Dato */}
        {showAddPunto && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Aggiungi Punto Dato</h3>
                <button onClick={() => setShowAddPunto(false)} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
              </div>
              <form onSubmit={handleAddPunto} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Nome *</label>
                  <input value={formPunto.nome} onChange={e => setFormPunto(p => ({ ...p, nome: e.target.value }))} required
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Indirizzo Registro</label>
                    <input value={formPunto.indirizzo_registro} onChange={e => setFormPunto(p => ({ ...p, indirizzo_registro: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Tipo Dato</label>
                    <select value={formPunto.tipo_dato} onChange={e => setFormPunto(p => ({ ...p, tipo_dato: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                      <option value="float">Float</option>
                      <option value="integer">Integer</option>
                      <option value="boolean">Boolean</option>
                      <option value="string">String</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Unità di Misura</label>
                    <input value={formPunto.unita_misura} onChange={e => setFormPunto(p => ({ ...p, unita_misura: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Zona</label>
                    <select value={formPunto.zona_id} onChange={e => setFormPunto(p => ({ ...p, zona_id: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                      <option value="">— Zona —</option>
                      {zone.map(z => <option key={z.id} value={z.id}>{z.nome}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowAddPunto(false)} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
                  <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold disabled:opacity-50">
                    {saving ? 'Salvataggio...' : 'Salva'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirm */}
        {deleteConfirm.open && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-sm shadow-2xl">
              <h3 className="text-lg font-semibold text-white mb-2">Elimina</h3>
              <p className="text-secondary-color text-sm mb-5">Sei sicuro di voler eliminare <strong className="text-white">{deleteConfirm.name}</strong>?</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm({ open: false, id: '', name: '', table: '' })} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
                <button onClick={handleDeleteBms} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-semibold">Elimina</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
